import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useTranslation } from "react-i18next";
import PublicLanguageSelector from "@/components/PublicLanguageSelector";
import ReCAPTCHA from "react-google-recaptcha";
import { Mail, Phone, User, Linkedin, UploadCloud, FileText, XCircle, CheckCircle } from "lucide-react";
import confetti from "canvas-confetti";


export default function PublicJobPage() {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const [job, setJob] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({
        FirstName: "",
        LastName: "",
        Email: "",
        Phone: "",
    });
    const [linkedin, setLinkedin] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitResult, setSubmitResult] = useState<null | { success: boolean; message: string }>(null);
    const [cvFile, setCvFile] = useState<File | null>(null);
    const [otherDocuments, setOtherDocuments] = useState<File[]>([]);
    const [uploading, setUploading] = useState(false);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [isCvDragging, setIsCvDragging] = useState(false);
    const [isOtherDocsDragging, setIsOtherDocsDragging] = useState(false);
    const cvFileInputRef = useRef<HTMLInputElement>(null);
    const otherDocsInputRef = useRef<HTMLInputElement>(null);
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [cvFilePath, setCvFilePath] = useState<string | null>(null);
    const [otherDocumentsPaths, setOtherDocumentsPaths] = useState<string[]>([]);
    const [customStyle, setCustomStyle] = useState<any>(null);

    useEffect(() => {
        async function fetchJob() {
            setLoading(true);
            const { data, error } = await supabase
                .from("jobs_public")
                .select("*")
                .eq("job_id", id)
                .single();
            if (data) {
                setJob(data);
            }
            setLoading(false);
        }
        if (id) fetchJob();
    }, [id]);

    useEffect(() => {
        if (submitResult && submitResult.success) {
            confetti({
                particleCount: 120,
                spread: 80,
                origin: { y: 0.7 },
                colors: ["#6366f1", "#60a5fa", "#fbbf24", "#34d399", "#f472b6"]
            });
        }
    }, [submitResult]);

    useEffect(() => {
        async function fetchCustomStyle() {
            if (!job?.org_id) return;
            const { data } = await supabase
                .from("custom_publicstyle")
                .select("customstyle")
                .eq("organization_id", job.org_id)
                .single();
            if (data && data.customstyle) setCustomStyle(data.customstyle);
        }
        fetchCustomStyle();
    }, [job?.org_id]);

    usePageTitle(job ? ` - ${job.title}` : t("jobs.public.jobPage.loading"), job?.org_name);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.name === "LinkedIn")
            setLinkedin(e.target.value);
        else
            setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            setCvFile(file);
        }
    };

    const handleOtherDocumentsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const files = Array.from(e.target.files);
            setOtherDocuments(files);
        }
    };

    // Generic drag handlers
    const handleDrag = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };
    
    const handleCvDrop = (e: React.DragEvent<HTMLLabelElement>) => {
        handleDrag(e);
        setIsCvDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const syntheticEvent = { target: { files: e.dataTransfer.files } } as unknown as React.ChangeEvent<HTMLInputElement>;
            handleFileChange(syntheticEvent);
            e.dataTransfer.clearData();
        }
    };
    
    const handleOtherDocsDrop = (e: React.DragEvent<HTMLLabelElement>) => {
        handleDrag(e);
        setIsOtherDocsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const syntheticEvent = { target: { files: e.dataTransfer.files } } as unknown as React.ChangeEvent<HTMLInputElement>;
            handleOtherDocumentsChange(syntheticEvent);
            e.dataTransfer.clearData();
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!job) return;

        // Client-side validation: Check if CV is provided
        if (!cvFile) {
            setSubmitResult({ success: false, message: t("jobs.public.jobPage.cvRequired") });
            return;
        }

        setSubmitting(true);
        setSubmitResult(null);

        try {
            // Execute reCAPTCHA v3 and get the token
            let token = captchaToken;
            if (recaptchaRef.current) {
                token = await recaptchaRef.current.executeAsync();
                setCaptchaToken(token);
            }

            if (!token) {
                setSubmitResult({ success: false, message: t("jobs.public.jobPage.captchaRequired", "Please complete the CAPTCHA.") });
                setSubmitting(false);
                return;
            }

            setUploading(true);

            // Create FormData to send files along with form data
            const formData = new FormData();
            formData.append('job_id', job.job_id);
            formData.append('org_id', job.org_id);
            formData.append('FirstName', form.FirstName);
            formData.append('LastName', form.LastName);
            formData.append('Email', form.Email);
            formData.append('Phone', form.Phone);
            formData.append('LinkedIn', linkedin);
            formData.append('captchaToken', token);

            // Add CV file if present
            if (cvFile) {
                formData.append('cv', cvFile);
            }

            // Add other documents if present
            if (otherDocuments.length > 0) {
                otherDocuments.forEach(doc => {
                    formData.append('otherDocuments', doc);
                });
            }

            setUploading(false);

            // Call edge function with FormData
            const { data, error } = await supabase.functions.invoke('apply-to-job', {
                body: formData,
            });

            // Improved error handling for user-friendly messages
            if (error) {
                // If we get a 409 status, it means the candidate has already applied
                if (error.status === 409) {
                    setSubmitResult({
                        success: false,
                        message: t("jobs.public.jobPage.alreadyApplied")
                    });
                    setSubmitting(false);
                    return;
                }

                // Check for 409 status code first
                if (error.status === 409) {
                    setSubmitResult({
                        success: false,
                        message: t("jobs.public.jobPage.alreadyApplied")
                    });
                    return;
                }

                // For other errors, try to get a meaningful error message
                let errorMessage = '';
                if (error.error instanceof Error) {
                    errorMessage = error.error.message;
                } else if (error.error) {
                    errorMessage = error.error;
                } else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error.message && !error.message.includes("non-2xx status code")) {
                    errorMessage = error.message;
                }

                setSubmitResult({ 
                    success: false, 
                    message: errorMessage || t("jobs.public.jobPage.submissionFailed")
                });
                setSubmitting(false);
                return;
            } else if (data?.success) {
                setSubmitResult({ success: true, message: t("jobs.public.jobPage.applicationSubmittedSuccess") });
                setForm({ FirstName: "", LastName: "", Email: "", Phone: "" });
                setLinkedin("");
                setCvFile(null);
                setOtherDocuments([]);
                setCaptchaToken(null);
                if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                }
                // Clear file inputs
                if (cvFileInputRef.current) cvFileInputRef.current.value = "";
                if (otherDocsInputRef.current) otherDocsInputRef.current.value = "";
            } else {
                setSubmitResult({ success: false, message: (data?.error || t("jobs.public.jobPage.submissionFailed")) });
            }
        } catch (err) {
            setSubmitResult({ success: false, message: t("jobs.public.jobPage.networkError") });
        } finally {
            setSubmitting(false);
            setUploading(false);
        }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center">{t("jobs.public.jobPage.loading")}</div>;
    if (!job) return <div className="min-h-screen flex items-center justify-center text-red-500">{t("jobs.public.jobPage.jobNotFound")}</div>;

    // Helper to get color or fallback
    const mainColor = customStyle?.mainColor || '#5a7ebf'; // digger-blue
    const bgColor = customStyle?.bgColor || 'hsl(210 40% 98%)'; // app background
    const pageStyle = customStyle?.pageStyle || 'square';
    const borderRadius = pageStyle === 'square' ? '0' : pageStyle === 'rounded' ? '1rem' : '0.25rem';

    return (
        <>
            <div
                className="min-h-screen py-8 px-4"
                style={{
                    backgroundColor: bgColor,
                }}
            >
                <div className="w-full max-w-7xl mx-auto flex justify-end mb-4">
                    <PublicLanguageSelector />
                </div>
                <div className="w-full max-w-7xl mx-auto flex flex-col md:flex-row gap-8 bg-transparent mt-8 mb-8">
                    {/* Logo (top left, above card) */}
                    {customStyle?.logoUrl && (
                        <div className="absolute left-0 top-0 ml-8 mt-4 z-10">
                            <img
                                src={customStyle.logoUrl}
                                alt="Organization Logo"
                                style={{ maxHeight: 48, width: 'auto', maxWidth: 160, marginBottom: 16 }}
                                className="block"
                            />
                        </div>
                    )}
                    {/* Left: Job Description */}
                    <div className="flex-1 md:basis-2/3 bg-white/80 backdrop-blur-lg shadow-sm p-8 md:p-10 border border-gray-200" style={{ borderRadius }}>
                        <div>
                            <div className="mb-8">
                                <Link
                                    to={customStyle?.jobsUrl && customStyle.jobsUrl.trim() !== '' ? customStyle.jobsUrl : `/jobs/org/${job.org_id}/public`}
                                    className="inline-flex items-center font-medium mb-6"
                                    style={{ color: mainColor }}
                                >
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                    </svg>
                                    {t("jobs.public.jobPage.seeAllJobs", { orgName: job.org_name })}
                                </Link>
                                <div className="text-center">
                                    <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-1 leading-tight">{job.title}</h1>
                                    {job.org_name && (
                                        <div className="text-lg md:text-xl font-semibold mb-2" style={{ color: mainColor }}>{job.org_name}</div>
                                    )}
                                </div>
                            </div>
                            <div className="prose max-w-none mb-0 text-gray-800" style={{ fontSize: '1.08rem' }}
                                dangerouslySetInnerHTML={{ __html: job.jobdesc_public || `<em>${t("jobs.public.jobPage.noDescriptionAvailable")}</em>` }}
                            />
                        </div>
                    </div>
                    {/* Right: Application Form */}
                    <div className="flex-1 md:basis-1/3">
                        <div className="sticky top-8">
                            <div className="bg-white/80 backdrop-blur-lg shadow-sm p-6 border border-gray-200" style={{ borderRadius }}>
                                {/* Show confirmation only if submitResult.success, else show form */}
                                {submitResult && submitResult.success ? (
                                    <div className="flex flex-col items-center justify-center min-h-[300px]">
                                        <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
                                        <div className="text-lg font-semibold text-green-700 mb-2 text-center">{submitResult.message}</div>
                                        <div className="mt-6 flex items-center text-gray-500 text-sm">
                                            <span className="mr-2">{t("jobs.public.jobPage.poweredBy")}</span>
                                            <a href="https://digger-ai.be" target="_blank" rel="noopener noreferrer" className="flex items-center gap-0">
                                                <div className="w-12 h-12 rounded-sm flex items-center justify-center overflow-hidden">
                                                    <img src="/img/NewDiggerIcon.png" alt="Digger" className="w-full h-full object-contain" />
                                                </div>
                                                <span className="text-2xl font-black tracking-tight text-gray-700">digger<span className="inline-block w-2 h-2 rounded-full bg-[#5a7ebf] ml-1 align-middle" /></span>
                                            </a>
                                        </div>
                                    </div>
                                ) : (
                                    <form className="space-y-4" onSubmit={handleSubmit}>
                                        <h2 className="text-xl font-semibold mb-4 text-gray-900">{t("jobs.public.jobPage.applyForJob")}</h2>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium mb-1" htmlFor="FirstName">{t("jobs.public.jobPage.firstName")} <span className="text-red-500">*</span></label>
                                                <div className="relative">
                                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                                    <input id="FirstName" className="w-full border border-gray-200 px-3 py-2 pl-10 bg-white focus:ring-2 focus:ring-blue-400 focus:outline-none" type="text" name="FirstName" value={form.FirstName} onChange={handleChange} required disabled={submitting} style={{ borderRadius: pageStyle === 'square' ? '0' : '0.375rem' }} />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1" htmlFor="LastName">{t("jobs.public.jobPage.lastName")} <span className="text-red-500">*</span></label>
                                                <div className="relative">
                                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                                    <input id="LastName" className="w-full border border-gray-200 px-3 py-2 pl-10 bg-white focus:ring-2 focus:ring-blue-400 focus:outline-none" type="text" name="LastName" value={form.LastName} onChange={handleChange} required disabled={submitting} style={{ borderRadius: pageStyle === 'square' ? '0' : '0.375rem' }} />
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1" htmlFor="Email">{t("jobs.public.jobPage.email")} <span className="text-red-500">*</span></label>
                                            <div className="relative">
                                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                                <input id="Email" className="w-full border border-gray-200 px-3 py-2 pl-10 bg-white focus:ring-2 focus:ring-blue-400 focus:outline-none" type="email" name="Email" value={form.Email} onChange={handleChange} required disabled={submitting} style={{ borderRadius: pageStyle === 'square' ? '0' : '0.375rem' }} />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1" htmlFor="Phone">{t("jobs.public.jobPage.phone")} <span className="text-red-500">*</span></label>
                                            <div className="relative">
                                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                                <input id="Phone" className="w-full border border-gray-200 px-3 py-2 pl-10 bg-white focus:ring-2 focus:ring-blue-400 focus:outline-none" type="tel" name="Phone" value={form.Phone} onChange={handleChange} required disabled={submitting} style={{ borderRadius: pageStyle === 'square' ? '0' : '0.375rem' }} />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1" htmlFor="LinkedIn">{t("jobs.public.jobPage.linkedinProfile")}</label>
                                            <div className="relative">
                                                <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                                <input
                                                    id="LinkedIn"
                                                    className="w-full border border-gray-200 px-3 py-2 pl-10 bg-white focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                                    type="url"
                                                    name="LinkedIn"
                                                    value={linkedin}
                                                    onChange={handleChange}
                                                    disabled={submitting}
                                                    style={{ borderRadius: pageStyle === 'square' ? '0' : '0.375rem' }}
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium">{t("jobs.public.jobPage.uploadCV")} <span className="text-red-500">*</span></label>
                                            <label 
                                                htmlFor="cv-upload" 
                                                className={`cursor-pointer bg-white border-2 border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center w-full transition-all ${isCvDragging ? 'border-blue-500 border-dashed bg-blue-50' : ''}`}
                                                style={{ borderRadius: pageStyle === 'square' ? '0' : '0.375rem' }}
                                                onDragEnter={(e) => { handleDrag(e); setIsCvDragging(true); }}
                                                onDragLeave={(e) => { handleDrag(e); setIsCvDragging(false); }}
                                                onDragOver={handleDrag}
                                                onDrop={handleCvDrop}
                                            >
                                                <UploadCloud className="w-5 h-5 mr-2" />
                                                <span>{cvFile ? t("jobs.public.jobPage.cvSelected") : t("jobs.public.jobPage.uploadCV")}</span>
                                            </label>
                                            <input
                                                id="cv-upload"
                                                ref={cvFileInputRef}
                                                className="hidden"
                                                type="file"
                                                name="cv"
                                                accept=".pdf,.doc,.docx"
                                                onChange={handleFileChange}
                                                disabled={submitting || uploading}
                                                required
                                            />
                                            {cvFile && (
                                                <div className="flex items-center justify-between text-sm text-gray-600 mt-1 bg-gray-50 p-2" style={{ borderRadius: pageStyle === 'square' ? '0' : '0.375rem' }}>
                                                    <div className="flex items-center">
                                                        <FileText className="h-5 w-5 mr-2 text-gray-500" />
                                                        <span className="font-medium">{cvFile.name}</span>
                                                    </div>
                                                    <span className="text-xs">({(cvFile.size / 1024).toFixed(1)} KB)</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium">{t("jobs.public.jobPage.uploadOtherDocuments")}</label>
                                            <label 
                                                htmlFor="other-docs-upload" 
                                                className={`cursor-pointer bg-white border-2 border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center w-full transition-all ${isOtherDocsDragging ? 'border-blue-500 border-dashed bg-blue-50' : ''}`}
                                                style={{ borderRadius: pageStyle === 'square' ? '0' : '0.375rem' }}
                                                onDragEnter={(e) => { handleDrag(e); setIsOtherDocsDragging(true); }}
                                                onDragLeave={(e) => { handleDrag(e); setIsOtherDocsDragging(false); }}
                                                onDragOver={handleDrag}
                                                onDrop={handleOtherDocsDrop}
                                            >
                                                <UploadCloud className="w-5 h-5 mr-2" />
                                                <span>{otherDocuments.length > 0 ? t("jobs.public.jobPage.otherDocumentsSelected", { count: otherDocuments.length }) : t("jobs.public.jobPage.uploadOtherDocuments")}</span>
                                            </label>
                                            <input
                                                id="other-docs-upload"
                                                ref={otherDocsInputRef}
                                                className="hidden"
                                                type="file"
                                                name="otherDocuments"
                                                accept=".pdf,.doc,.docx"
                                                multiple
                                                onChange={handleOtherDocumentsChange}
                                                disabled={submitting || uploading}
                                            />
                                            {otherDocuments.length > 0 && (
                                                <div className="text-sm text-gray-600 mt-1 space-y-1">
                                                    {otherDocuments.map((file, index) => (
                                                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2" style={{ borderRadius: pageStyle === 'square' ? '0' : '0.375rem' }}>
                                                            <div className="flex items-center">
                                                               <FileText className="h-5 w-5 mr-2 text-gray-500" />
                                                               <span className="font-medium">{file.name}</span>
                                                            </div>
                                                            <span className="text-xs">({(file.size / 1024).toFixed(1)} KB)</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        
                                        <button
                                            type="submit"
                                            className={`w-full py-2.5 font-semibold transition-all duration-300 ease-in-out ${submitting || uploading ? "opacity-60 cursor-not-allowed" : "hover:scale-105"}`}
                                            style={{
                                                background: mainColor,
                                                color: '#fff',
                                                borderRadius: pageStyle === 'square' ? '0' : '0.375rem', // 0.375rem = rounded-md
                                            }}
                                            disabled={submitting || uploading}
                                        >
                                            {submitting || uploading ? (
                                                <div className="flex items-center justify-center">
                                                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                                    {t("jobs.public.jobPage.submitting")}
                                                </div>
                                            ) : (
                                                t("jobs.public.jobPage.submitApplication")
                                            )}
                                        </button>
                                        
                                        <div className="flex justify-center mt-2">
                                            <div className="transform scale-50 origin-center relative" style={{ width: 256, height: 60 }}>
                                                <ReCAPTCHA
                                                    ref={recaptchaRef}
                                                    sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || "YOUR_RECAPTCHA_SITE_KEY"}
                                                    size="invisible"
                                                    badge="inline"
                                                />
                                                {/* Overlay to dim the captcha */}
                                                <div
                                                    className="absolute inset-0 bg-white bg-opacity-50 rounded"
                                                    style={{ pointerEvents: 'none' }}
                                                />
                                            </div>
                                        </div>
                                        
                                        {submitResult && (
                                            <div className={`mt-4 p-4 flex items-start text-sm ${submitResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`} style={{ borderRadius: pageStyle === 'square' ? '0' : '0.5rem' }}>
                                                {submitResult.success ? <CheckCircle className="h-5 w-5 mr-3 flex-shrink-0" /> : <XCircle className="h-5 w-5 mr-3 flex-shrink-0" />}
                                                <p>{submitResult.message}</p>
                                            </div>
                                        )}
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Footer - moved inside main container to prevent iframe scrollbar issues */}
            <div className="w-full max-w-7xl mx-auto mt-8">
                <footer className="border-t border-gray-200 py-4 flex flex-col items-center text-xs text-gray-500">
                    <span className="mb-1">{t("jobs.public.jobPage.poweredBy")}</span>
                    <a href="https://digger-ai.be" target="_blank" rel="noopener noreferrer" className="flex items-center gap-0 mt-1">
                        <div className="w-14 h-14 rounded-sm flex items-center justify-center overflow-hidden">
                            <img src="/img/NewDiggerIcon.png" alt="Digger" className="w-full h-full object-contain" />
                        </div>
                        <span className="text-3xl font-black tracking-tight text-gray-700">digger<span className="inline-block w-2 h-2 rounded-full bg-[#5a7ebf] ml-1 align-middle" /></span>
                    </a>
                </footer>
            </div>
        </>
    );
}